import { gsap} from 'gsap';
import { IDimOrientation, LayoutDimData, ResizeHelper } from '../../../../core/utils/ResizeHelper';
import { UniPlayer, UniversalPlayer } from '../../../../core/displayobject/UniversalPlayer';
import { MathHelper } from '../../../../core/utils/MathHelper';
import { ISequencePlay, Sequence } from '../../../../core/displayobject/Sequence';
import { SpinePlayConfig, SpinePlayer } from '../../../../core/displayobject/SpinePlayer';
import { Helper } from '../../../../core/utils/Helper';
import { SoundConfig } from '../../../sound/SoundConfig';
import { PunkRocker } from '../../../PunkRocker';
import { BLEND_MODES } from 'pixi.js';
import {SoundManager} from "../../../sound/SoundManager";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {PREngineManager} from "../../../punkEngine/PREngineManager";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";

export class FeaturePosterState {
    public static readonly POLICE:string = "Police";
    public static readonly BONUS:string = "Poster_Disobey";
    public static readonly BONUS2:string = "Poster_Chaos";
    public static readonly BONUS3:string = "Poster_MassTransitBBQS";
}

export class NoWinPosterState {
    public static readonly NO_WIN:string = "No_Win";
    public static readonly BOLLOCKS:string = "Bollocks";
    public static readonly QUEEN:string = "Queen";
    public static readonly SHIT_GAME:string = "Shit_Game";
}

export class BonusGameEndState {
    public static readonly BG_END:string = "BG_End";
}

export class BigWinPosterState {


    //BIGWIN
    public static readonly BIG_WIN:string[] = ["Poster_Bigwin1","Poster_Bigwin2","Poster_Bigwin3","Poster_Bigwin4", "Poster_Bigwin5"];

    // RETRIGGER
    public static readonly RETRIGGER:string = "Retrigger";

    public static bigWinLvl(lvl:number):number{
        return Math.min(lvl,4);
    }

    public static bigWinTypeLvl(lvl:number):string{
        return this.BIG_WIN[this.bigWinLvl(lvl)];
    }
}

export class PosterManager extends PIXI.Container {

    public get dim():LayoutDimData{ return ResizeHelper.calcDim(this.PosterDim); };
    private PosterDim:IDimOrientation = {
        landscape :{
            scale:      new PIXI.Point( 1, 1),
            position:   new PIXI.Point( 360, 360),
        },
        portrait :{
            scale:      new PIXI.Point( 1, 1),
            position:   new PIXI.Point( 360, 220)
        }
    };

    private WIDTH:number = 1440;
    private HEIGHT:number = 900;

    private static _instance:PosterManager;
    private player:UniversalPlayer;
    private postObj:PIXI.spine.Spine | null;
    
    private blackGraphics:PIXI.Graphics;
    private _bottomTrainVisible: boolean = false;
    //private _isRespin:boolean=false;

    private gradientOverlay:PIXI.Sprite;
    private gradientSupported:boolean = true;

    protected fireSequence:Sequence;

    protected sirenSpine:SpinePlayer;
    protected fsOutroTrainSpine:SpinePlayer;
    protected additionalSpinsSpine:SpinePlayer;

    private timeline:gsap.core.Timeline | undefined;
    private timelineBlack:gsap.core.Timeline;
    private _spines: PIXI.spine.Spine[];

    public static get instance():PosterManager{
        return this._instance;
    }

    constructor(){
        super();
        PosterManager._instance = this;
        this.name = "PosterManager";
        this._spines = [];
        //this._isRespin = false;
        this.initAnimations();
    };

    public gradientColorLvl(lvl:number):string{
        switch(lvl){
            case 0:
                return "rgb(0,51,255,0.7)";
            case 1:
                return "rgb(44,168,22,0.7)";
            case 2:
                return "rgb(204,0,0,0.7)";
            case 3:
                return "rgb(153,0,255,0.7)";
            default:
                return "rgb(0,0,0,0.7)"
        }
    }

    protected initAnimations():void{

        this.createGraphics();

        try{
            this.gradientOverlay = this.addChild(new PIXI.Sprite(this.createGradTexture(this.gradientColorLvl(0))));
            this.gradientOverlay.anchor.set(0.5,0.5);
            this.gradientOverlay.rotation = MathHelper.degreeToAngle(90);
            this.gradientOverlay.alpha = 0;
        }
        catch{
            this.gradientSupported = false;
        }

        this.fireSequence = this.addChild(new Sequence({texturePath:"BigWinFire"}));
        this.fireSequence.blendMode = BLEND_MODES.ADD;
        this.fireSequence.anchor.set(0.5,0.5);
        this.fireSequence.alpha = 0;

        this.player = new UniversalPlayer([
            // --------
            FeaturePosterState.POLICE,
            //FeaturePosterState.RESPIN,
            FeaturePosterState.BONUS,
            FeaturePosterState.BONUS2,
            FeaturePosterState.BONUS3,
            // --------
            BigWinPosterState.BIG_WIN[0],
            BigWinPosterState.BIG_WIN[1],
            BigWinPosterState.BIG_WIN[2],
            BigWinPosterState.BIG_WIN[3],
            BigWinPosterState.BIG_WIN[4],
            // --------
            BonusGameEndState.BG_END
        ]);

        this.player.uniPlayers.forEach(p => {
            this.addChild(p.obj);
            p.obj.alpha = 0;
            p.obj.name = p.name;
        });
        this.sirenSpine = this.addChild(new SpinePlayer(FeaturePosterState.POLICE));
        this.fsOutroTrainSpine = this.addChild(new SpinePlayer(BonusGameEndState.BG_END));
        this.additionalSpinsSpine = this.addChild(new SpinePlayer(BigWinPosterState.RETRIGGER));
        this.onResize();
    }

    protected createGradTexture(color:string):PIXI.Texture {
        // adjust it if somehow you need better quality for very very big images
        const quality = 126;
        const canvas = document.createElement('canvas');
        canvas.width = quality;
        canvas.height = 1;
    
        const ctx = canvas.getContext('2d');
    
        // use canvas2d API to create gradient
        const grd = ctx!.createLinearGradient(0, 0, quality, 0);
        grd.addColorStop(0, color);
        grd.addColorStop(0.75, 'black');
    
        ctx!.fillStyle = grd;
        ctx!.fillRect(0, 0, quality, 1);
    
        return PIXI.Texture.from(canvas);
    }

    private getSpine(level: number): PIXI.spine.Spine {
        let spine: PIXI.spine.Spine = new PIXI.spine.Spine(PunkRocker.assetLoader.getSpineAsset("Poster_Bigwin" + level.toString()));
        spine.position.set(0, 0);
        spine.name = "BIGWIN_SPINE_LEVEL_" + level;
        this._spines.push(spine);
        spine.visible = false;
        return spine;
    }

    public playBigWin(lvlRequest:number):gsap.core.Timeline{
        this.timeline = Helper.setTimeline(this.timeline);
        const spine: PIXI.spine.Spine = this.getSpine(lvlRequest+1);
        this.postObj = spine;
        this.timeline.add(()=>{{
            this.addChild(spine);
            if(lvlRequest>0){
                SoundManager.stopSoundEffect(`bigwinLevel${lvlRequest}`);
                this._spines[lvlRequest - 1].visible = false;
            }
            spine.visible = true;
            spine.state.setAnimation(0, "Intro", false);
            spine.state.setAnimation(1, "Loop", true);
            spine.stateData.setMix("Loop", "Outro", 0.4);
            SoundManager.playSoundEffect(`bigwinLevel${lvlRequest+1}`);
        }});
        let bigwinLevel = BigWinPosterState.bigWinLvl(lvlRequest);

/*
        if(this.postObj) this.postObj.stop();
        this.postObj = this.player.get(BigWinPosterState.bigWinTypeLvl(lvlRequest));

        const loopTrack:string = "Loop";
        const artemHack:string = "Level_Up";

        (this.postObj.obj as SpinePlayer).visible = true;
        (this.postObj.obj as SpinePlayer).spine.state.setAnimation(0, "Intro", false);
        (this.postObj.obj as SpinePlayer).spine.stateData.setMix(loopTrack, "Outro", 0.4);
        (this.postObj.obj as SpinePlayer).spine.stateData.setMix(loopTrack, artemHack, 0.4);
        (this.postObj.obj as SpinePlayer).spine.state.addAnimation(0, loopTrack, true, 0);
*/
        if(this.gradientSupported){
            this.gradientOverlay.texture = this.createGradTexture(this.gradientColorLvl(bigwinLevel));
            this.timeline.fromTo(this.gradientOverlay,{alpha:0}, {duration:0.25, alpha:1},0)
        }else{
            this.timeline.fromTo(this.blackGraphics,{alpha:0},{duration:0.25, alpha:0.4},0)
        }

        this.timeline.fromTo([this.fireSequence, spine],{alpha:0}, {duration:0.25, alpha:1},0)
        this.fireSequence.play({repeat:-1});

        this.fireSequence.scale.y = 2 + bigwinLevel;

        return this.timeline;
    }

    public forceFadeGradientOverlay(): void {
        this.blackGraphics.alpha = 0;
    }

    public play(state:string, conf?:ISequencePlay | SpinePlayConfig, fadeIn?:number, gradientLvl?:number):gsap.core.Timeline{
        this.timeline = Helper.setTimeline(this.timeline);
        //this.timeline.add(this.postObj.play(conf));
        if(state === BigWinPosterState.BIG_WIN[0] && this.gradientSupported){
            this.gradientOverlay.texture = this.createGradTexture(this.gradientColorLvl(BigWinPosterState.bigWinLvl(gradientLvl ? gradientLvl : 0)));

            if(fadeIn != undefined)
                this.timeline.fromTo(this.gradientOverlay,{alpha:0}, {duration:fadeIn, alpha:1},0)
            else
                this.gradientOverlay.alpha = 1;
        }else{
            if(fadeIn != undefined)
                this.timeline.fromTo(this.blackGraphics,{alpha:0},{duration:fadeIn, alpha:0.4},0)
            else
                this.blackGraphics.alpha = 0.4;
        }

        return this.timeline;
    }

    public stop(fadeout:number = 0.2,playEnd?:boolean):gsap.core.Timeline{
        if(!this.postObj) return Helper.setTimeline()
        /*if(this._isRespin){
            this._isRespin = false;
        }*/
        this.timeline = Helper.setTimeline(this.timeline, new gsap.core.Timeline({
            onComplete:() => {
                this.postObj = null;
                this._spines.forEach((spine: PIXI.spine.Spine) => {
                    spine.destroy();
                });
                this._spines = [];
            }
        }));
        this.timeline.add(gsap.to(this.postObj, {alpha:0, duration:fadeout}), 0);
        if(this.gradientSupported && this.gradientOverlay.alpha > 0) this.timeline.to(this.gradientOverlay, {duration:fadeout, alpha:0},0)
        this.timeline.to([this.fireSequence, this.blackGraphics],{duration:fadeout,alpha:0},0)
        return this.timeline;
    }

    public setBlackScreenAlpha(to:number,duration:number = 0.3):gsap.core.Timeline{
        this.timelineBlack = Helper.setTimeline(this.timelineBlack,new gsap.core.Timeline());
        this.timelineBlack.to(this.blackGraphics,{duration:duration,alpha:to});
        return this.timelineBlack;
    }

    private createGraphics():void{
        this.blackGraphics = new PIXI.Graphics();
        this.blackGraphics.alpha = 0;
        this.blackGraphics.interactive = false;
        this.blackGraphics.buttonMode = false;

        this.blackGraphics.beginFill( 0x000000, 1);
        this.blackGraphics.drawRect(-this.WIDTH,-this.HEIGHT,this.WIDTH*2, this.HEIGHT*2);
        this.blackGraphics.endFill();

        this.addChild(this.blackGraphics);
    }

    onResize(){
        if(this.blackGraphics === undefined) return;

        if(this.gradientSupported && this.gradientOverlay){
            this.gradientOverlay.width = ResizeHelper.resizeData.height;
            this.gradientOverlay.height = ResizeHelper.resizeData.width;            
            this.gradientOverlay.position.y = 360 - this.dim.position.y;
        }


        this.fireSequence.scale.x = ResizeHelper.isLandscape ? 3 : 2;
        this.fireSequence.position.y = ResizeHelper.isLandscape ? 250 : 660;

        this.position = this.dim.position;
        this.scale = this.dim.scale;

        //this.player.get(FeaturePosterState.RESPIN).obj.position.y = NolimitApplication.isLandscape?-270:-128;
        // if(this.signShown){
        //     const obj = this.player.get(FeaturePosterState.POSTER).obj;
        //     if(!ResizeHelper.isLandscape && obj.position.y <= 310){
        //         obj.position.y += 140;
        //     }else if(ResizeHelper.isLandscape && obj.position.y >= 310){
        //         obj.position.y -= 140;
        //     }
        // }

        const scaledReelDim: LayoutDimData = this._bottomTrainVisible ? PREngineManager.dimScaled : PREngineManager.dim;

        this.sirenSpine.scale.set(scaledReelDim.scale.x, 1);
        if(NolimitApplication.isLandscape) {
            this.sirenSpine.position.set(0, 0);
        }else{
            this.sirenSpine.position.set(0, 180);
        }
    }

    public onBottomTrainShow(flag: boolean, isRestore: boolean = false): void {
        this._bottomTrainVisible = flag;
        this.onResize();
    }

    public playSiren(active:boolean,level:number= 0, durr:number = 0.2):void{
        if(active){
            this.sirenSpine.visible = true;
            gsap.fromTo(this.sirenSpine, { alpha: 0 }, { alpha: 1, duration: durr });
            const trackName:string = level == 0 ? "PoliceFS_Loop_A" : "PoliceFS_Loop_B";
            this.sirenSpine.play({animation:trackName,loop:true});
        }else{
            gsap.fromTo(this.sirenSpine, { alpha: 1 }, { alpha: 0, duration: durr }).eventCallback("onComplete",() =>{
                this.sirenSpine.stop();
            });
        }
    }

    public playOutroTrain(active:boolean, durr:number = 0.2):void{
        if(active){
            this.fsOutroTrainSpine.visible = true;
            gsap.fromTo(this.fsOutroTrainSpine, { alpha: 0 }, { alpha: 1, duration: durr });
            gsap.to(this, {
                duration: durr + 0.1,
                onComplete:()=>{
                    SoundManager.stopMainAmbiance();
                    SoundManager.playAmbientLoop(GameMode.NORMAL);
                    PunkRocker.background.fromBonusToMain();
                }
            });
            this.fsOutroTrainSpine.play({
                animation: "bonusGameToMainGame",
                loop: false
            }).eventCallback("onComplete",() =>{
                this.fsOutroTrainSpine.stop();
            });
        }else{
            gsap.fromTo(this.fsOutroTrainSpine, {
                alpha: 1
            }, {
                alpha: 0,
                duration: durr
            }).eventCallback("onComplete",() =>{
                this.fsOutroTrainSpine.stop();
            });
        }
    }

    public playFSRetrigger(state:string):void{
        SoundManager.playSoundEffect(SoundConfig.FS_UPGRADE);
        this.additionalSpinsSpine.visible = true;
        this.additionalSpinsSpine.alpha = 1;
        this.additionalSpinsSpine.play({animation:state,loop:false});
    }
}